import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useIntercomDispatcher } from '@/hooks/comm/use-comm';
import { type ResponseError, responseScheme } from '@/models/response';
import { useAuthStore } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';
import { avatarUrlSchema, nameSchema } from '@/utils/schemas';

export default function useSignIn() {
  const { dispatch } = useIntercomDispatcher();

  const mutation = useMutation<SignInResponse, ResponseError, SignInRequest>({
    mutationKey: ['/PhoneLogin'],
    mutationFn: (params) =>
      fetcher<SignInResponse>('/PhoneLogin', params, {
        requireAuth: false,
        schema: signInResponseSchema,
        validResponseCodes: [0],
      }),
    retry: false,
    onSuccess: (response) => {
      if (response.Code === 0 && response.UID) {
        useAuthStore.getState().start({
          UserID: response.UID,
          NickName: response.NickName,
          AvatarUrl: response.HeadImg,
          Token: response.Token || response.AccessToken || '',
          RefreshToken: response.RefreshToken || '',
        });
        dispatch({ event: 'update-auth-credentials' });
      }
    },
    onError: (error) => {
      addToast({
        type: 'error',
        message: error.message,
      });
    },
  });

  return {
    ...mutation,
    signIn: (data: SignInRequest & { WithOtp?: boolean }) =>
      mutation.mutateAsync({
        Phone: data.Phone,
        TelCode: data.TelCode,
        [data.WithOtp ? 'Code' : 'Password']: data.Password,
      }),
    isSignedIn: mutation.data?.Code === 0,
  };
}

export const signInRequestSchema = z.object({
  Phone: z.string().regex(/^\d+$/),
  TelCode: z.string(),
  Password: z.string().min(6).optional(),
  Code: z.string().min(6).optional(),
});

type SignInRequest = z.infer<typeof signInRequestSchema>;

const signInResponseSchema = responseScheme
  .merge(
    z.object({
      VChatID: z.string().nullish(),
      UID: z.number().nullish(),
      Token: z.string().nullish(),
      AccessToken: z.string().nullish(),
      RefreshToken: z.string().nullish(),
      TelCode: z.string().nullish(),
      Phone: z.string().nullish(),
      NickName: nameSchema,
      HeadImg: avatarUrlSchema,
    }),
  )
  .strip();

type SignInResponse = z.infer<typeof signInResponseSchema>;
